import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ArrowLeft, Building2, Mail, Phone, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { MerchantProfileTab } from './tabs/MerchantProfileTab';
import { NotificationEmailsPanel } from '@/components/merchant/NotificationEmailsPanel';
import { MerchantKycTab } from './tabs/MerchantKycTab';
import { MerchantBankingTab } from './tabs/MerchantBankingTab';
import { MerchantBillingTab } from './tabs/MerchantBillingTab';
import { MerchantPricingTab } from './tabs/MerchantPricingTab';
import { MerchantCouriersTab } from './tabs/MerchantCouriersTab';
import { MerchantWalletTab } from './tabs/MerchantWalletTab';
import { MerchantChannelsTab } from './tabs/MerchantChannelsTab';
import { MerchantLinkedAccountsTab } from './tabs/MerchantLinkedAccountsTab';
import { MerchantWarehousesTab } from './tabs/MerchantWarehousesTab';
import { MerchantWhatsappTab } from './tabs/MerchantWhatsappTab';
import { LabelSettingsPanel } from '@/features/labels/LabelSettingsPanel';
import { WebhookManager } from '@/features/webhooks/WebhookManager';
import { SignedImage } from '@/components/ui/SignedImage';
import { MerchantWmsToggle } from '@/components/merchant/MerchantWmsToggle';
import { MerchantStatusToggle } from '@/components/merchant/MerchantStatusToggle';
import { useAuth, canModuleTab, isRestricted } from '@/hooks/useAuth';
import { loginAsMerchant } from './impersonate';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'kyc', label: 'KYC' },
  { id: 'banking', label: 'Banking' },
  { id: 'billing', label: 'Billing' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'couriers', label: 'Couriers' },
  { id: 'warehouses', label: 'Pickup Locations' },
  { id: 'wallet', label: 'Wallet' },
  { id: 'channels', label: 'Channels' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'labels', label: 'Labels' },
  { id: 'linked-accounts', label: 'Linked Accounts' },
] as const;

type TabId = typeof TABS[number]['id'];

interface Merchant {
  id: number;
  companyName: string;
  brandName?: string;
  email: string;
  mobile?: string;
  status: string;
  serviceType?: string;
  billingMode?: string;
  postpaidLimit?: number;
  remittanceTatDays?: number;
  logoUrl?: string;
  walletBalance?: number;
  createdAt: string;
}

export function MerchantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  // Passwords are argon2 hashes and are never readable. Support opens the merchant's own portal
  // instead ("Login as"), which is audited by the impersonation token and expires in 6 hours.
  const { user } = useAuth();
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';
  const [loginAsOpen, setLoginAsOpen] = useState(false);
  const [adminPwd, setAdminPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const merchantId = Number(id);

  const { data: m, isLoading } = useQuery({
    queryKey: ['merchant', merchantId],
    queryFn: async () => (await api.get<Merchant>(`/merchants/${merchantId}`)).data,
    enabled: !!merchantId,
  });

  const doLoginAs = async () => {
    if (!m || !adminPwd) return;
    setBusy(true);
    try {
      await loginAsMerchant({ id: merchantId, email: m.email, companyName: m.companyName }, adminPwd);
      toast.success(`Opening ${m.companyName} as merchant (new tab)`);
      setLoginAsOpen(false);
      setAdminPwd('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not log in as this merchant');
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!m) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <p className="text-slate-500">Merchant not found.</p>
        <Link to="/merchants" className="text-primary text-sm mt-2 inline-block">← Back to merchants</Link>
      </div>
    );
  }

  // A role holding only Merchants → Pending Approvals may open an account it is reviewing (still
  // PENDING), not the rest of the merchant master.
  if (isRestricted(user) && !canModuleTab(user, 'Merchants', 'All Merchants', 'view') && m.status !== 'PENDING') {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <p className="text-slate-500">You don't have permission to view this merchant (Merchants → All Merchants).</p>
        <Link to="/merchants" className="text-primary text-sm mt-2 inline-block">← Back to merchants</Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link to="/merchants" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-3">
        <ArrowLeft size={14} /> Back to merchants
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary overflow-hidden flex-shrink-0">
              {/* logoUrl holds an R2 key now, not a URL — a raw <img src> can't render it. */}
              <SignedImage src={m.logoUrl} alt={m.companyName} className="w-full h-full object-contain"
                fallback={<Building2 size={24} />} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{m.companyName}</h1>
              {m.brandName && <p className="text-sm text-slate-500">{m.brandName}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Mail size={12} /> {m.email}</span>
                {m.mobile && <span className="flex items-center gap-1"><Phone size={12} /> {m.mobile}</span>}
                {isAdmin && (
                  <button
                    onClick={() => { setLoginAsOpen(true); setAdminPwd(''); }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition border border-indigo-200"
                    title="Open this merchant's portal as them (your admin password required)">
                    <LogIn size={12} /> Login as
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="text-right space-y-1">
            <StatusBadge status={m.status} />
            <ServiceTypeBadges value={m.serviceType} />
            {m.billingMode && <div className="text-xs text-slate-500 mt-1">{m.billingMode}</div>}
          </div>
        </div>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <MerchantStatusToggle merchantId={merchantId} status={m.status} />
        <MerchantWmsToggle merchantId={merchantId} initial={(m as any).wmsEnabled} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 px-4 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition ${
                activeTab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === 'profile' && <MerchantProfileTab merchant={m} />}
          {activeTab === 'kyc' && <MerchantKycTab merchantId={merchantId} />}
          {/* Same panel the merchant edits under their own Settings — one definition, so the two
              sides cannot disagree about where this client's invoices are sent. */}
          {activeTab === 'notifications' && <NotificationEmailsPanel merchantId={merchantId} />}
          {activeTab === 'whatsapp' && <MerchantWhatsappTab merchantId={merchantId} />}
          {activeTab === 'banking' && <MerchantBankingTab merchantId={merchantId} />}
          {activeTab === 'billing' && <MerchantBillingTab merchantId={merchantId} />}
          {activeTab === 'pricing' && <MerchantPricingTab merchantId={merchantId} />}
          {activeTab === 'couriers' && <MerchantCouriersTab merchantId={merchantId} />}
          {activeTab === 'warehouses' && <MerchantWarehousesTab merchantId={merchantId} />}
          {activeTab === 'wallet' && <MerchantWalletTab merchantId={merchantId} />}
          {activeTab === 'channels' && <MerchantChannelsTab merchantId={merchantId} />}
          {activeTab === 'webhooks' && <WebhookManager merchantId={merchantId} />}
          {activeTab === 'labels' && <LabelSettingsPanel merchantId={merchantId} embedded />}
          {activeTab === 'linked-accounts' && <MerchantLinkedAccountsTab merchantId={merchantId} />}
        </div>
      </div>

      {/* Admin-gated "Login as merchant" — confirms with the admin's OWN password, then opens the
          merchant portal in a new tab on a 6-hour impersonation token. */}
      {loginAsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => { setLoginAsOpen(false); setAdminPwd(''); }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-indigo-100 text-indigo-700 p-2 rounded-lg"><LogIn size={18} /></div>
              <h3 className="text-lg font-semibold">Login as {m.companyName}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Opens this merchant&apos;s portal in a new tab, signed in as them. The session lasts 6 hours,
              shows an &quot;impersonated&quot; banner, and does not affect your own login.
            </p>
            <input type="password" placeholder="your admin password" value={adminPwd} autoFocus
              onChange={(e) => setAdminPwd(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doLoginAs()}
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-primary" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setLoginAsOpen(false); setAdminPwd(''); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={doLoginAs} disabled={busy || !adminPwd}
                className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
                Open merchant portal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceTypeBadges({ value }: { value?: string }) {
  if (!value) return null;
  const types: string[] = value === 'BOTH' ? ['B2C', 'B2B'] : [value];
  return (
    <div className="flex items-center gap-1 justify-end">
      {types.map((t) => (
        <span
          key={t}
          className={`text-xs px-2 py-0.5 rounded-full font-medium border ${t === 'B2C' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
          {t}
        </span>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    APPROVED: 'bg-green-50 text-green-700 border-green-200',
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    REJECTED: 'bg-red-50 text-red-700 border-red-200',
    SUSPENDED: 'bg-slate-100 text-slate-600 border-slate-200',
    ACTIVE: 'bg-green-50 text-green-700 border-green-200',
    INACTIVE: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      {status}
    </span>
  );
}
